import React, { useState, useEffect, useMemo } from "react";
import styled from "styled-components";

const PAGE_SIZE = 30;

const FunnelContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
`;

const FilterContainer = styled.div`
  width: 100%;
  max-width: 600px;
  margin-bottom: 20px;
  padding: 15px;
  background: #f5f5f5;
  border-radius: 8px;

  @media (max-width: 768px) {
    max-width: 100%;
  }
`;

const FilterLabel = styled.label`
  font-weight: bold;
  margin-right: 10px;
  display: block;
  margin-bottom: 8px;
`;

const FilterInput = styled.input`
  padding: 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  width: 100%;
  max-width: 400px;

  @media (max-width: 768px) {
    max-width: 100%;
  }
`;

const FilterInputRow = styled.div`
  display: flex;
  align-items: center;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    gap: 8px;
  }
`;

const FilterButton = styled.button`
  padding: 8px 16px;
  background: #4caf50;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  margin-left: 10px;
  &:hover {
    background: #45a049;
  }
  &:disabled {
    background: #ccc;
    cursor: default;
  }

  @media (max-width: 768px) {
    margin-left: 0;
  }
`;

const ClearButton = styled.button`
  padding: 8px 16px;
  background: #666;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  margin-left: 10px;
  &:hover {
    background: #555;
  }

  @media (max-width: 768px) {
    margin-left: 0;
  }
`;

const FunnelStage = styled.div`
  width: 100%;
  max-width: 600px;
  padding: 20px;
  background: linear-gradient(to right, #4caf50, #66bb6a);
  color: white;
  border-radius: 8px;
  text-align: center;
  position: relative;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
`;

const StageTitle = styled.div`
  font-size: 18px;
  font-weight: bold;
  margin-bottom: 10px;
`;

const StageValue = styled.div`
  font-size: 32px;
  font-weight: bold;
`;

const StageConversion = styled.div`
  font-size: 14px;
  margin-top: 10px;
  opacity: 0.9;
`;

const Arrow = styled.div`
  font-size: 24px;
  color: #4caf50;
  margin: -10px 0;
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: 40px;
  color: #666;
`;

const TableScrollWrapper = styled.div`
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
`;

const FunnelTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-top: 20px;
`;

const TableHeader = styled.thead`
  background: #f5f5f5;
`;

const TableRow = styled.tr`
  &:nth-child(even) {
    background: #f9f9f9;
  }
  &:hover {
    background: #f0f0f0;
    cursor: pointer;
  }
`;

const SortableHeaderCell = styled.th`
  padding: 12px;
  text-align: left;
  font-weight: bold;
  border-bottom: 2px solid #ddd;
  cursor: pointer;
  user-select: none;
  white-space: nowrap;
  &:hover {
    background: #eee;
  }
`;

const TableCell = styled.td`
  padding: 12px;
  border-bottom: 1px solid #eee;

  @media (max-width: 768px) {
    padding: 8px;
  }
`;

const PaginationBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin-top: 16px;
  font-size: 14px;
  color: #666;
`;

const SORTABLE_COLUMNS = [
  { key: "restaurantName", label: "Restaurant Name" },
  { key: "restaurantClicks", label: "Clicks" },
  { key: "restaurantDetailsClicks", label: "Details" },
  { key: "restaurantOptionsScrolls", label: "Scrolls" },
  { key: "restaurantWebsiteClicks", label: "Website" },
  { key: "restaurantDirectionsClicks", label: "Directions" },
  { key: "detailsConversionRate", label: "Details Conv. %" },
  { key: "overallConversionRate", label: "Overall Conv. %" },
];

const EngagementFunnel = ({ data, loading, currentRestaurantId, onRestaurantFilterChange }) => {
  const [restaurantFilter, setRestaurantFilter] = useState(currentRestaurantId || "");
  const [filterText, setFilterText] = useState("");
  const [sortColumn, setSortColumn] = useState("restaurantClicks");
  const [sortDirection, setSortDirection] = useState("desc");
  const [currentPage, setCurrentPage] = useState(0);

  useEffect(function syncRestaurantFilterFromProps() {
    setRestaurantFilter(currentRestaurantId || "");
  }, [currentRestaurantId]);

  // Reset page when filter or sort changes
  useEffect(function resetPageOnFilterOrSortChange() {
    setCurrentPage(0);
  }, [filterText, sortColumn, sortDirection]);

  // Processed data: filter -> sort -> paginate
  const { pageData, totalFiltered, totalPages } = useMemo(() => {
    if (!Array.isArray(data)) return { pageData: [], totalFiltered: 0, totalPages: 0 };

    // Filter
    let filtered = data;
    if (filterText) {
      const lower = filterText.toLowerCase();
      filtered = data.filter(
        (f) =>
          (f.restaurantName || "").toLowerCase().includes(lower) ||
          (f.restaurantId || "").toLowerCase().includes(lower)
      );
    }

    // Sort
    const sorted = [...filtered].sort((a, b) => {
      let aVal = a[sortColumn];
      let bVal = b[sortColumn];

      // Convert string rates to numbers for comparison
      if (typeof aVal === "string" && !isNaN(aVal)) aVal = parseFloat(aVal);
      if (typeof bVal === "string" && !isNaN(bVal)) bVal = parseFloat(bVal);

      // String comparison for name
      if (sortColumn === "restaurantName") {
        aVal = (aVal || "").toLowerCase();
        bVal = (bVal || "").toLowerCase();
        return sortDirection === "asc"
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      }

      return sortDirection === "asc" ? aVal - bVal : bVal - aVal;
    });

    const total = sorted.length;
    const pages = Math.ceil(total / PAGE_SIZE);
    const sliced = sorted.slice(currentPage * PAGE_SIZE, (currentPage + 1) * PAGE_SIZE);

    return { pageData: sliced, totalFiltered: total, totalPages: pages };
  }, [data, filterText, sortColumn, sortDirection, currentPage]);

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortColumn(column);
      setSortDirection("desc");
    }
  };

  const sortIndicator = (column) => {
    if (sortColumn !== column) return "";
    return sortDirection === "asc" ? " \u25B2" : " \u25BC";
  };

  if (loading) {
    return <LoadingMessage>Loading funnel data...</LoadingMessage>;
  }

  if (!data) {
    return <LoadingMessage>No engagement funnel data available.</LoadingMessage>;
  }

  // Shared filter bar for both views
  const filterBar = (
    <FilterContainer>
      <FilterLabel>
        {Array.isArray(data) ? "Filter by Name or ID:" : "Filter by Restaurant ID:"}
      </FilterLabel>
      <FilterInputRow>
        {Array.isArray(data) ? (
          <FilterInput
            type="text"
            placeholder="Search by restaurant name or ID"
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
          />
        ) : (
          <>
            <FilterInput
              type="text"
              placeholder="Enter restaurant ID or leave empty for all"
              value={restaurantFilter}
              onChange={(e) => setRestaurantFilter(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === "Enter") {
                  onRestaurantFilterChange(restaurantFilter || null);
                }
              }}
            />
            <FilterButton
              onClick={() => onRestaurantFilterChange(restaurantFilter || null)}
            >
              Filter
            </FilterButton>
          </>
        )}
        {(filterText || restaurantFilter) && (
          <ClearButton onClick={() => {
            setFilterText("");
            setRestaurantFilter("");
            if (!Array.isArray(data)) {
              onRestaurantFilterChange(null);
            }
          }}>
            Clear
          </ClearButton>
        )}
      </FilterInputRow>
    </FilterContainer>
  );

  // Array (all restaurants) view
  if (Array.isArray(data)) {
    return (
      <FunnelContainer>
        {filterBar}

        <div style={{ fontSize: "13px", color: "#888", alignSelf: "flex-start" }}>
          Showing {pageData.length} of {totalFiltered} restaurants
          {filterText && ` matching "${filterText}"`}
        </div>

        <TableScrollWrapper>
          <FunnelTable>
            <TableHeader>
              <tr>
                {SORTABLE_COLUMNS.map((col) => (
                  <SortableHeaderCell
                    key={col.key}
                    onClick={() => handleSort(col.key)}
                  >
                    {col.label}{sortIndicator(col.key)}
                  </SortableHeaderCell>
                ))}
              </tr>
            </TableHeader>
            <tbody>
              {pageData.map((funnel, index) => (
                <TableRow
                  key={funnel.restaurantId || index}
                  onClick={() => onRestaurantFilterChange(funnel.restaurantId)}
                >
                  <TableCell>{funnel.restaurantName || "Unknown"}</TableCell>
                  <TableCell>{funnel.restaurantClicks.toLocaleString()}</TableCell>
                  <TableCell>{funnel.restaurantDetailsClicks.toLocaleString()}</TableCell>
                  <TableCell>{funnel.restaurantOptionsScrolls.toLocaleString()}</TableCell>
                  <TableCell>{(funnel.restaurantWebsiteClicks || 0).toLocaleString()}</TableCell>
                  <TableCell>{(funnel.restaurantDirectionsClicks || 0).toLocaleString()}</TableCell>
                  <TableCell>{funnel.detailsConversionRate}%</TableCell>
                  <TableCell>{funnel.overallConversionRate}%</TableCell>
                </TableRow>
              ))}
            </tbody>
          </FunnelTable>
        </TableScrollWrapper>

        {totalPages > 1 && (
          <PaginationBar>
            <FilterButton
              onClick={() => setCurrentPage((p) => Math.max(0, p - 1))}
              disabled={currentPage === 0}
            >
              Previous
            </FilterButton>
            <span>Page {currentPage + 1} of {totalPages}</span>
            <FilterButton
              onClick={() => setCurrentPage((p) => Math.min(totalPages - 1, p + 1))}
              disabled={currentPage >= totalPages - 1}
            >
              Next
            </FilterButton>
          </PaginationBar>
        )}
      </FunnelContainer>
    );
  }

  // Single restaurant funnel view
  return (
    <FunnelContainer>
      {filterBar}

      {data.restaurantName && (
        <div style={{ textAlign: "center", marginBottom: "10px", fontSize: "18px", fontWeight: "bold", color: "#333" }}>
          Restaurant: {data.restaurantName}
        </div>
      )}

      <FunnelStage style={{ width: "100%" }}>
        <StageTitle>Restaurant Clicks</StageTitle>
        <StageValue>{data.restaurantClicks.toLocaleString()}</StageValue>
      </FunnelStage>

      <Arrow>&darr;</Arrow>

      <FunnelStage style={{ width: "85%" }}>
        <StageTitle>Restaurant Details Clicks</StageTitle>
        <StageValue>{data.restaurantDetailsClicks.toLocaleString()}</StageValue>
        <StageConversion>
          {data.detailsConversionRate}% conversion rate
        </StageConversion>
      </FunnelStage>

      <Arrow>&darr;</Arrow>

      <FunnelStage style={{ width: "70%" }}>
        <StageTitle>Restaurant Options Scrolls</StageTitle>
        <StageValue>{data.restaurantOptionsScrolls.toLocaleString()}</StageValue>
        <StageConversion>
          {data.optionsConversionRate}% conversion rate
        </StageConversion>
      </FunnelStage>

      <Arrow>&darr;</Arrow>

      <div style={{ display: "flex", gap: "20px", width: "55%" }}>
        <FunnelStage style={{ flex: 1 }}>
          <StageTitle>Website Clicks</StageTitle>
          <StageValue>{(data.restaurantWebsiteClicks || 0).toLocaleString()}</StageValue>
          <StageConversion>
            {data.websiteConversionRate || 0}% from details
          </StageConversion>
        </FunnelStage>

        <FunnelStage style={{ flex: 1 }}>
          <StageTitle>Directions Clicks</StageTitle>
          <StageValue>{(data.restaurantDirectionsClicks || 0).toLocaleString()}</StageValue>
          <StageConversion>
            {data.directionsConversionRate || 0}% from details
          </StageConversion>
        </FunnelStage>
      </div>

      <div style={{ marginTop: "20px", textAlign: "center", color: "#666" }}>
        <strong>Overall Conversion:</strong> {data.overallConversionRate}% from clicks to scrolls
      </div>
    </FunnelContainer>
  );
};

export default EngagementFunnel;
